/**
 * Automatic Features Panel Component
 *
 * Settings panel for configuring presence automatic features
 * Shows controls for auto-heartbeat, activity tracking, and idle detection
 * Part of PresenceManager configuration UI
 */

import { forwardRef } from 'react';
import { Stack, Paper, Text, Switch } from '@/components/ui';
import { usePresenceManager } from '@/hooks/usePresenceManager';
import { TextInput } from '@mantine/core';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface AutomaticFeaturesPanelProps {
  /**
   * Panel title
   */
  title?: string;
  /**
   * Show paper border
   */
  withBorder?: boolean;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * Settings panel for configuring presence automatic features
 * Provides controls for auto-heartbeat, activity tracking, and idle detection
 *
 * @example
 * ```tsx
 * <AutomaticFeaturesPanel />
 * <AutomaticFeaturesPanel title="Presence Settings" />
 * ```
 */
export const AutomaticFeaturesPanel = forwardRef<HTMLDivElement, AutomaticFeaturesPanelProps>(
  (props, ref) => {
    const {
      title = 'Automatic Features',
      withBorder = true,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const {
      config,
      updateConfig,
      lastHeartbeat,
      lastActivity,
      formattedIdleTime,
    } = usePresenceManager();

    return (
      <Paper p="md" withBorder={withBorder} ref={ref} className={className}>
        <Stack gap="md">
          <Text size="lg" fw={700} style={accessibilityStyles}>
            {title}
          </Text>

          {/* Auto-Heartbeat */}
          <Stack gap="xs">
            <Switch
              label="Auto-Heartbeat"
              description="Automatically send heartbeat to keep presence active"
              checked={config.autoHeartbeat}
              onChange={(e) => updateConfig({ autoHeartbeat: e.currentTarget.checked })}
            />
            {config.autoHeartbeat && (
              <>
                <TextInput
                  label="Heartbeat Interval (seconds)"
                  type="number"
                  value={config.heartbeatInterval}
                  onChange={(e) => updateConfig({ heartbeatInterval: Number(e.currentTarget.value) })}
                  min={10}
                  max={300}
                  step={10}
                  size="sm"
                />
                {lastHeartbeat && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    Last heartbeat: {lastHeartbeat.toLocaleTimeString()}
                  </Text>
                )}
              </>
            )}
          </Stack>

          {/* Activity Tracking */}
          <Stack gap="xs">
            <Switch
              label="Activity Tracking"
              description="Track mouse and keyboard activity"
              checked={config.activityTracking}
              onChange={(e) => updateConfig({ activityTracking: e.currentTarget.checked })}
            />
            {config.activityTracking && (
              <>
                <TextInput
                  label="Activity Debounce (ms)"
                  type="number"
                  value={config.activityDebounce}
                  onChange={(e) => updateConfig({ activityDebounce: Number(e.currentTarget.value) })}
                  min={1000}
                  max={30000}
                  step={1000}
                  size="sm"
                />
                {lastActivity && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    Last activity: {lastActivity.toLocaleTimeString()}
                  </Text>
                )}
              </>
            )}
          </Stack>

          {/* Idle Detection */}
          <Stack gap="xs">
            <Switch
              label="Idle Detection"
              description="Automatically switch to Away after inactivity"
              checked={config.idleDetection}
              onChange={(e) => updateConfig({ idleDetection: e.currentTarget.checked })}
            />
            {config.idleDetection && (
              <>
                <TextInput
                  label="Idle Timeout (minutes)"
                  type="number"
                  value={config.idleTimeout}
                  onChange={(e) => updateConfig({ idleTimeout: Number(e.currentTarget.value) })}
                  min={1}
                  max={60}
                  step={1}
                  size="sm"
                />
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Time until idle: {formattedIdleTime}
                </Text>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }
);

AutomaticFeaturesPanel.displayName = 'AutomaticFeaturesPanel';

export default AutomaticFeaturesPanel;
